/**
 * SocialDeepDive Plugin - Calculation Functions
 *
 * Core business logic for calculating totals and weighted averages
 * across all social media platforms.
 *
 * Key concept: Weighted Average CPM
 * When platforms have different CPMs, we calculate a weighted average
 * based on their volume (followers, impressions, or engagements).
 *
 * Example:
 *   Instagram: 50K followers @ 10 kr CPM
 *   Facebook: 50K followers @ 20 kr CPM
 *   Total: 100K followers
 *   Weighted CPM: (50K/100K × 10) + (50K/100K × 20) = 5 + 10 = 15 kr
 */

import { PlatformMetrics, TotalMetrics, DEFAULT_TOTALS } from './types';

/**
 * Calculate totals and weighted averages across all platforms
 *
 * @param platforms Array of platform metrics
 * @returns Total metrics with weighted average CPMs
 */
export function calculateTotals(platforms: PlatformMetrics[]): TotalMetrics {
  // Initialize totals
  const totals: TotalMetrics = {
    followers: 0,
    impressions: 0,
    engagements: 0,
    followers_cpm: 0,
    impressions_cpm: 0,
    engagements_cpm: 0,
    activePlatforms: {
      followers: 0,
      impressions: 0,
      engagements: 0,
    },
  };

  // Handle empty array
  if (!platforms || platforms.length === 0) {
    return DEFAULT_TOTALS;
  }

  // Sum volumes and count active platforms
  platforms.forEach((platform) => {
    // Sum volumes
    totals.followers += platform.followers || 0;
    totals.impressions += platform.impressions || 0;
    totals.engagements += platform.engagements || 0;

    // Count active platforms (platforms with data > 0)
    if (platform.followers > 0) totals.activePlatforms.followers++;
    if (platform.impressions > 0) totals.activePlatforms.impressions++;
    if (platform.engagements > 0) totals.activePlatforms.engagements++;
  });

  // Calculate weighted average CPMs
  totals.followers_cpm = calculateWeightedCPM(
    platforms,
    'followers',
    'followers_cpm',
    totals.followers
  );

  totals.impressions_cpm = calculateWeightedCPM(
    platforms,
    'impressions',
    'impressions_cpm',
    totals.impressions
  );

  totals.engagements_cpm = calculateWeightedCPM(
    platforms,
    'engagements',
    'engagements_cpm',
    totals.engagements
  );

  return totals;
}

/**
 * Calculate weighted average CPM for a specific metric
 *
 * Formula:
 * - If volume exists: Σ (platform_volume / total_volume × platform_cpm)
 * - If no volume but CPM values exist: Simple average of CPM values (for UX feedback)
 *
 * @param platforms Array of platform metrics
 * @param volumeKey The volume metric key (followers, impressions, or engagements)
 * @param cpmKey The CPM metric key (followers_cpm, impressions_cpm, or engagements_cpm)
 * @param totalVolume Total volume across all platforms
 * @returns Weighted average CPM (rounded to 2 decimals)
 */
export function calculateWeightedCPM(
  platforms: PlatformMetrics[],
  volumeKey: 'followers' | 'impressions' | 'engagements',
  cpmKey: 'followers_cpm' | 'impressions_cpm' | 'engagements_cpm',
  totalVolume: number
): number {
  // If there's volume, calculate weighted average (accurate)
  if (totalVolume > 0) {
    const weightedSum = platforms.reduce((sum, platform) => {
      const volume = platform[volumeKey] || 0;
      const cpm = platform[cpmKey] || 0;

      // Skip platforms with no volume
      if (volume === 0) return sum;

      // Calculate weight (percentage of total)
      const weight = volume / totalVolume;

      // Add weighted CPM to sum
      return sum + cpm * weight;
    }, 0);

    // Round to 2 decimal places
    return Math.round(weightedSum * 100) / 100;
  }

  // If no volume but CPM values exist, calculate simple average (for UX feedback)
  const cpmValues = platforms.map((p) => p[cpmKey] || 0).filter((cpm) => cpm > 0);

  if (cpmValues.length > 0) {
    const simpleAverage = cpmValues.reduce((sum, cpm) => sum + cpm, 0) / cpmValues.length;
    return Math.round(simpleAverage * 100) / 100;
  }

  // No volume and no CPM values
  return 0;
}

/**
 * Calculate the total media value for a single platform
 *
 * Formula:
 *   Followers Value = (followers / 1000) × followers_cpm
 *   Impressions Value = (impressions / 1000) × impressions_cpm
 *   Engagements Value = (engagements / 1000) × engagements_cpm
 *   Total = Followers Value + Impressions Value + Engagements Value
 *
 * @param platform Platform metrics
 * @returns Total media value for this platform
 */
export function calculatePlatformValue(platform: PlatformMetrics): number {
  const followersValue = (platform.followers / 1000) * platform.followers_cpm;
  const impressionsValue = (platform.impressions / 1000) * platform.impressions_cpm;
  const engagementsValue = (platform.engagements / 1000) * platform.engagements_cpm;

  return followersValue + impressionsValue + engagementsValue;
}

/**
 * Calculate total media value across all platforms
 *
 * @param platforms Array of platform metrics
 * @returns Total media value across all platforms
 */
export function calculateTotalValue(platforms: PlatformMetrics[]): number {
  return platforms.reduce((sum, platform) => {
    return sum + calculatePlatformValue(platform);
  }, 0);
}

/**
 * Validate platform metrics (all values must be >= 0)
 *
 * @param platform Platform metrics to validate
 * @returns True if valid, false otherwise
 */
export function validatePlatformMetrics(platform: PlatformMetrics): boolean {
  return (
    platform.followers >= 0 &&
    platform.followers_cpm >= 0 &&
    platform.impressions >= 0 &&
    platform.impressions_cpm >= 0 &&
    platform.engagements >= 0 &&
    platform.engagements_cpm >= 0
  );
}

/**
 * Validate array of platform metrics
 *
 * @param platforms Array of platform metrics to validate
 * @returns True if all platforms are valid, false otherwise
 */
export function validateAllPlatformMetrics(platforms: PlatformMetrics[]): boolean {
  return platforms.every(validatePlatformMetrics);
}

/**
 * Filter out platforms with no data (all values are 0)
 *
 * @param platforms Array of platform metrics
 * @returns Array of platforms with at least some data
 */
export function filterEmptyPlatforms(platforms: PlatformMetrics[]): PlatformMetrics[] {
  return platforms.filter(
    (platform) =>
      platform.followers > 0 ||
      platform.followers_cpm > 0 ||
      platform.impressions > 0 ||
      platform.impressions_cpm > 0 ||
      platform.engagements > 0 ||
      platform.engagements_cpm > 0
  );
}

/**
 * Get summary statistics for platform data
 *
 * @param platforms Array of platform metrics
 * @returns Summary statistics
 */
export function getPlatformSummary(platforms: PlatformMetrics[]): {
  totalPlatforms: number;
  activePlatforms: number;
  totalValue: number;
  averageValuePerPlatform: number;
} {
  const activePlatforms = filterEmptyPlatforms(platforms);
  const totalValue = calculateTotalValue(platforms);

  return {
    totalPlatforms: platforms.length,
    activePlatforms: activePlatforms.length,
    totalValue,
    averageValuePerPlatform: activePlatforms.length > 0 ? totalValue / activePlatforms.length : 0,
  };
}
